import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Settings as SettingsIcon, Save, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import PayoutsCard from "@/components/PayoutsCard";
import GenrePickerCard from "@/components/GenrePickerCard";
import MidiSettingsCard from "@/components/MidiSettingsCard";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [optout, setOptout] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    setOptout(!!user.digest_optout);
    // Only re-sync when the user IDENTITY changes, not on every AuthContext refresh
    // (otherwise typing/toggling gets reset by background refetches).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/me/digest/optout`, { optout });
      toast.success(optout ? "Digest emails paused." : "Digest emails enabled.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save preferences");
    } finally { setSaving(false); }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.get(`${API}/me/digest/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error("Preview failed");
    } finally { setPreviewing(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="settings-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center"><SettingsIcon className="w-5 h-5 text-black" strokeWidth={2.5}/></div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span> · SETTINGS</div>
        </Link>
        <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white">← Studio</Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-2 flex items-center gap-2"><SettingsIcon className="w-3 h-3"/> // Preferences</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Your settings</h1>
          <p className="text-[#94A3B8] mt-2">Fine-tune notifications for <span className="font-mono text-[#00F0FF]">{user.email}</span>.</p>
        </div>

        <PayoutsCard/>

        <div id="midi">
          <MidiSettingsCard/>
        </div>

        <GenrePickerCard/>

        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#00F0FF]"/>
            <div className="font-display font-black text-2xl">Weekly digest email</div>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Every Monday morning, get a recap of what the DJs you follow shipped last week — new sets, live sessions, and your own earnings if you're a DJ.
          </p>

          <label className="flex items-start gap-3 p-3 rounded-lg border border-[#2A354B] bg-[#0B0E14] cursor-pointer hover:border-[#00F0FF] transition-colors" data-testid="digest-optout-label">
            <input type="checkbox"
              data-testid="digest-optout-checkbox"
              checked={optout}
              onChange={e => setOptout(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#EF4444]"/>
            <div className="flex-1">
              <div className="text-sm font-bold">Pause my weekly digest</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">
                {optout ? "You will NOT receive the Monday recap." : "You will receive the Monday recap (if the DJs you follow were active)."}
              </div>
            </div>
          </label>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="digest-save-button" onClick={save} disabled={saving}
              className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-2"/> Save preferences</>}
            </Button>
            <Button data-testid="digest-preview-button" onClick={openPreview} disabled={previewing} variant="outline"
              className="border-[#2A354B] hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent text-white font-black uppercase tracking-widest">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Eye className="w-4 h-4 mr-2"/> Preview this week's digest</>}
            </Button>
          </div>

          {preview && (
            <div className="rounded-lg border border-[#1E2638] bg-[#0B0E14] p-4 space-y-2" data-testid="digest-preview-panel">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">// Preview · {preview.payload.dj_count} DJ{preview.payload.dj_count === 1 ? "" : "s"} followed</div>
                <button onClick={() => setPreview(null)} data-testid="digest-preview-close" className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] hover:text-white">✕ close</button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 rounded bg-[#0B0E14] border border-[#1E2638] text-center">
                  <div className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-widest">New sets</div>
                  <div className="font-display font-black text-2xl text-[#00F0FF]">{preview.payload.new_sets.length}</div>
                </div>
                <div className="p-2 rounded bg-[#0B0E14] border border-[#1E2638] text-center">
                  <div className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-widest">Live sessions</div>
                  <div className="font-display font-black text-2xl text-[#FF2A6D]">{preview.payload.replays.length}</div>
                </div>
                <div className="p-2 rounded bg-[#0B0E14] border border-[#1E2638] text-center">
                  <div className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-widest">Your earnings</div>
                  <div className="font-display font-black text-2xl text-[#FFC107]">${(preview.payload.own_earnings_cents / 100).toFixed(2)}</div>
                </div>
              </div>
              <iframe
                data-testid="digest-preview-iframe"
                srcDoc={preview.html}
                title="Digest preview"
                className="w-full h-96 rounded border border-[#1E2638] bg-white"
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
